import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/hooks/useAuth";

import { ScrollToTop } from "@/components/ScrollToTop";

// Eagerly load only the Index page for fastest FCP
import Index from "./pages/Index";

// Lazy-load all other routes for code splitting
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Shop = lazy(() => import("./pages/Shop"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Cart = lazy(() => import("./pages/Cart"));
const Booking = lazy(() => import("./pages/Booking"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const ClientGalleries = lazy(() => import("./pages/ClientGalleries"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminGalleries = lazy(() => import("./pages/admin/AdminGalleries"));
const AdminVendors = lazy(() => import("./pages/admin/AdminVendors"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminSubscribers = lazy(() => import("./pages/admin/AdminSubscribers"));
const AdminContracts = lazy(() => import("./pages/admin/AdminContracts"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminPrintful = lazy(() => import("./pages/admin/AdminPrintful"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons"));
const AdminSchedule = lazy(() => import("./pages/admin/AdminSchedule"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminEmailMarketing = lazy(() => import("./pages/admin/AdminEmailMarketing"));
const AdminEmailPreview = lazy(() => import("./pages/admin/AdminEmailPreview"));
const AdminSocialMedia = lazy(() => import("./pages/admin/AdminSocialMedia"));
const AdminImageAudit = lazy(() => import("./pages/admin/AdminImageAudit"));
const ContractSign = lazy(() => import("./pages/ContractSign"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Terms = lazy(() => import("./pages/Terms"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const RevealExperience = lazy(() => import("./pages/RevealExperience"));
const ConnectOnboarding = lazy(() => import("./pages/connect/ConnectOnboarding"));
const ConnectProducts = lazy(() => import("./pages/connect/ConnectProducts"));
const ConnectStorefront = lazy(() => import("./pages/connect/ConnectStorefront"));
const ConnectSuccess = lazy(() => import("./pages/connect/ConnectSuccess"));
const ConnectDashboard = lazy(() => import("./pages/connect/ConnectDashboard"));
const ConnectAccountStorefront = lazy(() => import("./pages/connect/ConnectAccountStorefront"));
const BusinessCardDesign = lazy(() => import("./pages/BusinessCardDesign"));
const FlyerDesign = lazy(() => import("./pages/FlyerDesign"));
const PrintShop = lazy(() => import("./pages/PrintShop"));
const PrintProductDetail = lazy(() => import("./pages/PrintProductDetail"));
const OklahomaPhotographer = lazy(() => import("./pages/OklahomaPhotographer"));
const SessionPrepGuide = lazy(() => import("./pages/SessionPrepGuide"));
const GiveawayThankYou = lazy(() => import("./pages/GiveawayThankYou"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

const PageFallback = () => <div className="min-h-screen bg-background" />;

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/about" element={<About />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/prints" element={<PrintShop />} />
                <Route path="/prints/:productId" element={<PrintProductDetail />} />
                <Route path="/product/:handle" element={<ProductDetail />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/booking/success" element={<BookingSuccess />} />
                <Route path="/galleries" element={<ClientGalleries />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="calendar" element={<AdminCalendar />} />
                  <Route path="bookings" element={<AdminBookings />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="invoices" element={<AdminInvoices />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="printful" element={<AdminPrintful />} />
                  <Route path="contracts" element={<AdminContracts />} />
                  <Route path="coupons" element={<AdminCoupons />} />
                  <Route path="galleries" element={<AdminGalleries />} />
                  <Route path="vendors" element={<AdminVendors />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="testimonials" element={<AdminTestimonials />} />
                  <Route path="subscribers" element={<AdminSubscribers />} />
                  <Route path="schedule" element={<AdminSchedule />} />
                  <Route path="analytics" element={<AdminAnalytics />} />
                  <Route path="email-marketing" element={<AdminEmailMarketing />} />
                  <Route path="email-preview" element={<AdminEmailPreview />} />
                  <Route path="social-media" element={<AdminSocialMedia />} />
                  <Route path="audit-logs" element={<AdminAuditLogs />} />
                  <Route path="image-audit" element={<AdminImageAudit />} />
                </Route>
                <Route path="/contract/sign/:contractId" element={<ContractSign />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/reveal-experience" element={<RevealExperience />} />
                <Route path="/connect/onboarding" element={<ConnectOnboarding />} />
                <Route path="/connect/products" element={<ConnectProducts />} />
                <Route path="/connect/storefront" element={<ConnectStorefront />} />
                <Route path="/connect/storefront/:accountId" element={<ConnectAccountStorefront />} />
                <Route path="/connect/success" element={<ConnectSuccess />} />
                <Route path="/connect/dashboard" element={<ConnectDashboard />} />
                <Route path="/business-card" element={<BusinessCardDesign />} />
                <Route path="/flyer" element={<FlyerDesign />} />
                <Route path="/oklahoma-photographer" element={<OklahomaPhotographer />} />
                <Route path="/portland-photographer" element={<Navigate to="/oklahoma-photographer" replace />} />
                <Route path="/session-prep-guide" element={<SessionPrepGuide />} />
                <Route path="/giveaway-thank-you" element={<GiveawayThankYou />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;